import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Zap, Sparkles, CalendarDays, Sun, Moon, PartyPopper } from "lucide-react";

/**
 * Modern rule builder with:
 *  - Preset chips (Weekend, Peak hours, Off-peak, Custom date, Holiday, Season)
 *  - Visual weekday selector · time slider · month grid · native date pickers
 *  - Server contract is preserved (rule_type + rule_value strings)
 */

const WEEKDAYS = [
  { i: 0, s: "Mon" }, { i: 1, s: "Tue" }, { i: 2, s: "Wed" },
  { i: 3, s: "Thu" }, { i: 4, s: "Fri" },
  { i: 5, s: "Sat" }, { i: 6, s: "Sun" },
];
const MONTHS = [
  { i: 1, s: "Jan" }, { i: 2, s: "Feb" }, { i: 3, s: "Mar" }, { i: 4, s: "Apr" },
  { i: 5, s: "May" }, { i: 6, s: "Jun" }, { i: 7, s: "Jul" }, { i: 8, s: "Aug" },
  { i: 9, s: "Sep" }, { i: 10, s: "Oct" }, { i: 11, s: "Nov" }, { i: 12, s: "Dec" },
];

// Preset shortcuts — the way most sports facilities actually think about pricing.
const PRESETS = [
  {
    key: "weekend",
    label: "Weekend surcharge",
    hint: "Higher price every Sat + Sun",
    icon: PartyPopper,
    apply: (form) => ({ ...form, rule_type: "weekend", rule_value: "", description: form.description || "Weekend surcharge" }),
  },
  {
    key: "evening_peak",
    label: "Evening peak",
    hint: "5pm – 10pm premium",
    icon: Moon,
    apply: (form) => ({ ...form, rule_type: "peak_hours", rule_value: "17-22", description: form.description || "Evening peak hours" }),
  },
  {
    key: "morning_early",
    label: "Early-morning discount",
    hint: "6am – 9am cheaper slot",
    icon: Sun,
    apply: (form) => ({ ...form, rule_type: "peak_hours", rule_value: "6-9", description: form.description || "Early-morning special" }),
  },
  {
    key: "weekday_offpeak",
    label: "Weekday off-peak",
    hint: "Mon–Fri lower rate",
    icon: CalendarDays,
    apply: (form) => ({ ...form, rule_type: "weekday", rule_value: "0,1,2,3,4", description: form.description || "Weekday off-peak" }),
  },
  {
    key: "holiday",
    label: "Public holiday",
    hint: "One specific date",
    icon: Sparkles,
    apply: (form) => ({ ...form, rule_type: "date", rule_value: new Date().toISOString().slice(0, 10), description: form.description || "Public holiday" }),
  },
  {
    key: "season",
    label: "Seasonal (date range)",
    hint: "e.g. summer / IPL",
    icon: CalendarDays,
    apply: (form) => {
      const start = new Date().toISOString().slice(0, 10);
      const endD = new Date(); endD.setDate(endD.getDate() + 30);
      return { ...form, rule_type: "date_range", rule_value: `${start}:${endD.toISOString().slice(0, 10)}`, description: form.description || "Seasonal rate" };
    },
  },
];

const EMPTY = {
  service_slug: "",
  rule_type: "weekend",
  rule_value: "",
  price_per_hour: "",
  gst_percent: 0,
  priority: 10,
  active: true,
  description: "",
};

// ---- helpers: encode / decode rule_value for the visual pickers ----
function parseWeekdays(v) {
  return new Set((v || "").split(",").map((x) => parseInt(x.trim())).filter((n) => !Number.isNaN(n)));
}
function parseMonths(v) {
  return new Set((v || "").split(",").map((x) => parseInt(x.trim())).filter((n) => !Number.isNaN(n)));
}
function parseHours(v) {
  const [lo, hi] = (v || "6-22").split("-").map((x) => parseInt(x.trim()));
  return { lo: Number.isNaN(lo) ? 6 : lo, hi: Number.isNaN(hi) ? 22 : hi };
}
function parseRange(v) {
  const [a, b] = (v || "").split(":");
  return { from: a || "", to: b || "" };
}

function RuleValueBuilder({ form, setForm }) {
  const type = form.rule_type;

  if (type === "weekend") {
    return (
      <div className="text-xs font-mono text-white/50 border border-white/12 bg-black/30 p-3">
        Applies to <span className="brand-blue">every Saturday and Sunday</span> automatically. No further input required.
      </div>
    );
  }

  if (type === "weekday") {
    const sel = parseWeekdays(form.rule_value);
    const toggle = (i) => {
      const s = new Set(sel);
      s.has(i) ? s.delete(i) : s.add(i);
      setForm({ ...form, rule_value: [...s].sort().join(",") });
    };
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Which weekday(s)?</div>
        <div className="flex flex-wrap gap-2" data-testid="rule-weekday-picker">
          {WEEKDAYS.map((d) => (
            <button
              key={d.i} type="button" onClick={() => toggle(d.i)}
              data-testid={`weekday-${d.s}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border ${
                sel.has(d.i) ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/50 hover:border-white/40"
              }`}
            >{d.s}</button>
          ))}
        </div>
      </div>
    );
  }

  if (type === "month") {
    const sel = parseMonths(form.rule_value);
    const toggle = (i) => {
      const s = new Set(sel);
      s.has(i) ? s.delete(i) : s.add(i);
      setForm({ ...form, rule_value: [...s].sort((a, b) => a - b).join(",") });
    };
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Which month(s)?</div>
        <div className="grid grid-cols-6 gap-2" data-testid="rule-month-picker">
          {MONTHS.map((m) => (
            <button
              key={m.i} type="button" onClick={() => toggle(m.i)}
              data-testid={`month-${m.s}`}
              className={`py-2 text-xs font-bold uppercase tracking-widest border ${
                sel.has(m.i) ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/50 hover:border-white/40"
              }`}
            >{m.s}</button>
          ))}
        </div>
      </div>
    );
  }

  if (type === "peak_hours") {
    const { lo, hi } = parseHours(form.rule_value);
    const update = (nlo, nhi) => setForm({ ...form, rule_value: `${nlo}-${nhi}` });
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Time window</div>
        <div className="flex items-center gap-3">
          <select value={lo} onChange={(e) => update(parseInt(e.target.value), hi)}
            data-testid="peak-from"
            className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
          <span className="text-white/40 text-xs font-mono">to</span>
          <select value={hi} onChange={(e) => update(lo, parseInt(e.target.value))}
            data-testid="peak-to"
            className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
          <span className="text-[10px] font-mono text-white/40">(inclusive)</span>
        </div>
      </div>
    );
  }

  if (type === "date") {
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Specific date</div>
        <input
          type="date" value={form.rule_value}
          onChange={(e) => setForm({ ...form, rule_value: e.target.value })}
          data-testid="rule-date"
          className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
        />
      </div>
    );
  }

  if (type === "date_range") {
    const { from, to } = parseRange(form.rule_value);
    const update = (nf, nt) => setForm({ ...form, rule_value: `${nf}:${nt}` });
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Date range</div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={from} onChange={(e) => update(e.target.value, to)}
            data-testid="range-from"
            className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm" />
          <span className="text-white/40 text-xs font-mono">to</span>
          <input type="date" value={to} onChange={(e) => update(from, e.target.value)}
            data-testid="range-to"
            className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm" />
        </div>
      </div>
    );
  }
  return null;
}

const RULE_TYPES = [
  { key: "weekend",    label: "Weekend" },
  { key: "weekday",    label: "Weekday(s)" },
  { key: "peak_hours", label: "Time window" },
  { key: "date",       label: "Specific date" },
  { key: "date_range", label: "Date range" },
  { key: "month",      label: "Month(s)" },
];

export default function AdminPricingRules() {
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState({ service_slug: "", date: new Date().toISOString().slice(0, 10), start_hour: 18, result: null });
  const [bulkWeekend, setBulkWeekend] = useState(false);

  const load = () => api.get("/admin/pricing-rules").then((r) => setItems(r.data));

  useEffect(() => {
    load();
    // Use effective service configs — new schema uses `catalog_type` (not `type`)
    api.get("/services").then((r) => {
      const hourly = r.data.filter((s) => s.catalog_type === "hourly");
      setServices(hourly);
      if (hourly.length) {
        setForm((f) => ({ ...f, service_slug: f.service_slug || hourly[0].slug }));
        setPreview((p) => ({ ...p, service_slug: p.service_slug || hourly[0].slug }));
      }
    });
  }, []);

  const currentService = useMemo(
    () => services.find((s) => s.slug === form.service_slug),
    [services, form.service_slug],
  );
  const defaultRate = currentService?.effective_hourly_rate || 0;

  const openNew = () => {
    const first = services[0]?.slug || "";
    setEditing({});
    setForm({ ...EMPTY, service_slug: first });
  };

  const save = async () => {
    // Client-side validation
    if (!form.service_slug) { toast.error("Pick a service"); return; }
    if (!form.price_per_hour) { toast.error("Enter a price per hour"); return; }
    if (form.rule_type !== "weekend" && !form.rule_value) {
      toast.error("Complete the rule details (pick day/date/hours)");
      return;
    }
    try {
      const payload = {
        ...form,
        price_per_hour: parseFloat(form.price_per_hour),
        gst_percent: 0,
        priority: parseInt(form.priority) || 10,
      };
      if (editing?.id) payload.id = editing.id;
      await api.post("/admin/pricing-rules", payload);
      toast.success("Rule saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this pricing rule?")) return;
    await api.delete(`/admin/pricing-rules/${id}`);
    load();
  };

  const runPreview = async () => {
    try {
      const { data } = await api.get("/admin/pricing-preview", { params: preview });
      setPreview({ ...preview, result: data });
    } catch (e) { toast.error(apiError(e)); }
  };

  const serviceLabel = (slug) => services.find((s) => s.slug === slug)?.name || slug;
  const readableRule = (r) => {
    const { rule_type, rule_value } = r;
    if (rule_type === "weekend") return "Every Sat + Sun";
    if (rule_type === "weekday") {
      const nums = parseWeekdays(rule_value);
      return [...nums].map((n) => WEEKDAYS[n]?.s).filter(Boolean).join(", ") || "Weekdays";
    }
    if (rule_type === "month") {
      const nums = parseMonths(rule_value);
      return [...nums].map((n) => MONTHS[n - 1]?.s).filter(Boolean).join(", ") || "Months";
    }
    if (rule_type === "peak_hours") {
      const { lo, hi } = parseHours(rule_value);
      return `${String(lo).padStart(2, "0")}:00 – ${String(hi).padStart(2, "0")}:00`;
    }
    if (rule_type === "date_range") {
      const { from, to } = parseRange(rule_value);
      return `${from} → ${to}`;
    }
    if (rule_type === "date") {
      try {
        return new Date(rule_value + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
      } catch (_e) { return rule_value; }
    }
    return rule_value || "—";
  };

  const noServices = services.length === 0;

  return (
    <div data-testid="admin-pricing-rules">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Dynamic Fee Plans</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Pricing Rules.</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Layer surcharges and discounts over your base hourly rate. Highest priority wins when multiple rules match.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setBulkWeekend(true)}
            disabled={noServices}
            data-testid="bulk-weekend-btn"
            className="border border-brand-orange text-brand-orange px-4 py-3 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:bg-brand-orange hover:text-black disabled:opacity-40"
          >
            <PartyPopper size={12} /> Bulk Weekend Surcharge
          </button>
          <button
            onClick={openNew}
            disabled={noServices}
            data-testid="new-rule-btn"
            className="bg-brand-blue text-white px-5 py-3 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue disabled:opacity-40"
          >
            <Plus size={12} /> New Rule
          </button>
        </div>
      </div>

      {noServices && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 mb-6 text-sm text-yellow-200" data-testid="no-hourly-services">
          No hourly rental services detected. Configure at least one service with billing type &quot;hour&quot; in{" "}
          <a href="/admin/service-pricing" className="brand-blue underline">Service Config</a>{" "}to create pricing rules.
        </div>
      )}

      {/* Live preview */}
      <div className="border border-white/12 bg-[#131A2B] p-6 mb-8" data-testid="pricing-preview-card">
        <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-4">// Test what a slot costs</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={preview.service_slug}
            onChange={(e) => setPreview({ ...preview, service_slug: e.target.value, result: null })}
            data-testid="preview-service"
            className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
          >
            {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
          <input
            type="date" value={preview.date}
            onChange={(e) => setPreview({ ...preview, date: e.target.value, result: null })}
            data-testid="preview-date"
            className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
          />
          <select
            value={preview.start_hour}
            onChange={(e) => setPreview({ ...preview, start_hour: parseInt(e.target.value), result: null })}
            data-testid="preview-hour"
            className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
          >
            {Array.from({ length: 17 }, (_, i) => 6 + i).map((h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
          <button
            onClick={runPreview}
            data-testid="preview-run"
            className="bg-brand-orange text-white py-2.5 text-xs uppercase font-bold tracking-widest flex items-center justify-center gap-2 hover:opacity-90"
          >
            <Zap size={12} /> Compute
          </button>
        </div>
        {preview.result && (
          <div className="mt-4 pt-4 border-t border-white/12 flex items-center justify-between gap-4 flex-wrap" data-testid="preview-result">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/40">
                Resolved rate for {preview.date} @ {String(preview.start_hour).padStart(2, "0")}:00
              </div>
              <div className="font-display text-3xl font-black brand-blue mt-1">
                {formatINR(preview.result.price_per_hour)}/hr <span className="text-white/40 text-sm font-mono uppercase ml-2">GST-exempt</span>
              </div>
            </div>
            <div className="text-right text-xs font-mono">
              {preview.result.applied_rule ? (
                <>
                  <div className="brand-orange uppercase tracking-widest">Rule applied</div>
                  <div className="mt-1">{preview.result.applied_rule.rule_type} · {preview.result.applied_rule.rule_value || "—"}</div>
                  <div className="text-white/40">{preview.result.applied_rule.description || "—"}</div>
                </>
              ) : (
                <div className="text-white/40 uppercase tracking-widest">Default catalog rate</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Rule list */}
      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">Service</th>
              <th className="text-left p-4">When</th>
              <th className="text-left p-4">Rule details</th>
              <th className="text-right p-4">Price / hr</th>
              <th className="text-right p-4">Priority</th>
              <th className="text-left p-4">Active</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} data-testid={`rule-${r.id}`} className="border-b border-white/5">
                <td className="p-4">{serviceLabel(r.service_slug)}</td>
                <td className="p-4 font-mono text-xs uppercase tracking-widest brand-orange">{r.rule_type.replace("_", " ")}</td>
                <td className="p-4 text-xs">{readableRule(r)}</td>
                <td className="p-4 text-right font-mono brand-blue">{formatINR(r.price_per_hour)}</td>
                <td className="p-4 text-right">{r.priority}</td>
                <td className="p-4">{r.active ? <span className="brand-blue">●</span> : <span className="text-white/30">○</span>}</td>
                <td className="p-4 text-right space-x-2">
                  <button
                    onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r, gst_percent: r.gst_percent ?? 0 }); }}
                    data-testid={`edit-rule-${r.id}`}
                    className="text-white/60 hover:brand-blue"
                  ><Edit3 size={14} /></button>
                  <button
                    onClick={() => del(r.id)}
                    data-testid={`del-rule-${r.id}`}
                    className="text-white/60 hover:text-red-400"
                  ><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-white/40">
                No custom pricing rules yet. Click <b className="brand-blue">&quot;New Rule&quot;</b> and pick a preset to get started.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#131A2B] border border-white/12 max-w-3xl w-full my-4 md:my-8 flex flex-col max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 md:p-8 pb-4 shrink-0 border-b border-white/10">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-1">// Fee plan</div>
                <h3 className="font-display text-2xl md:text-3xl uppercase font-black tracking-tight">{editing.id ? "Edit" : "New"} pricing rule</h3>
              </div>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 md:p-8 pt-4">

            {/* Presets */}
            {!editing.id && (
              <div className="mb-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Quick presets</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="rule-presets">
                  {PRESETS.map((p) => (
                    <button
                      key={p.key} type="button"
                      onClick={() => setForm(p.apply(form))}
                      data-testid={`preset-${p.key}`}
                      className="flex items-start gap-3 border border-white/12 p-3 hover:border-brand-blue hover:bg-brand-blue/5 text-left"
                    >
                      <p.icon size={16} className="brand-orange mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-bold">{p.label}</div>
                        <div className="text-[10px] text-white/40 font-mono">{p.hint}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Service + Rule type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Service</div>
                <select
                  value={form.service_slug}
                  onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
                  data-testid="rule-service"
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
                >
                  {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
                {currentService && (
                  <div className="text-[10px] font-mono text-white/40 mt-1">
                    Base rate: <span className="brand-blue">{formatINR(defaultRate)}/hr</span>
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Rule type</div>
                <select
                  value={form.rule_type}
                  onChange={(e) => setForm({ ...form, rule_type: e.target.value, rule_value: "" })}
                  data-testid="rule-type"
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
                >
                  {RULE_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
            </div>

            {/* Dynamic rule-value builder */}
            <div className="mb-6">
              <RuleValueBuilder form={form} setForm={setForm} />
            </div>

            {/* Price, Priority, Active */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="col-span-2 md:col-span-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Override price (₹ / hr)</div>
                <input
                  type="number"
                  value={form.price_per_hour}
                  onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })}
                  placeholder={defaultRate ? `Base: ${defaultRate}` : "e.g. 2500"}
                  data-testid="rule-price"
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
                />
                {form.price_per_hour && defaultRate > 0 && (
                  <div className="text-[10px] font-mono mt-1">
                    {parseFloat(form.price_per_hour) > defaultRate ? (
                      <span className="brand-orange">+{((parseFloat(form.price_per_hour) / defaultRate - 1) * 100).toFixed(0)}% vs base</span>
                    ) : parseFloat(form.price_per_hour) < defaultRate ? (
                      <span className="text-emerald-400">{((parseFloat(form.price_per_hour) / defaultRate - 1) * 100).toFixed(0)}% vs base</span>
                    ) : (
                      <span className="text-white/40">Same as base</span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Priority</div>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  data-testid="rule-priority"
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              <input
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description (e.g. Weekend evening premium)"
                data-testid="rule-description"
                className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm"
              />
              <label className="flex items-center gap-2 text-sm border border-white/12 px-4 py-2.5">
                <input
                  type="checkbox" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="rule-active"
                  className="accent-brand-blue"
                />
                <span>Rule is active</span>
              </label>
            </div>
            </div>

            <div className="p-4 md:p-6 border-t border-white/10 shrink-0 bg-[#131A2B]">
              <button
                onClick={save}
                data-testid="rule-save"
                className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue"
              >
                Save Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkWeekend && (
        <BulkWeekendModal
          services={services}
          onClose={() => setBulkWeekend(false)}
          onApplied={(msg) => { toast.success(msg); setBulkWeekend(false); load(); }}
        />
      )}
    </div>
  );
}

function BulkWeekendModal({ services, onClose, onApplied }) {
  const [selected, setSelected] = useState(() => new Set(services.map((s) => s.slug)));
  const [mode, setMode] = useState("surcharge_amount"); // surcharge_amount | surcharge_percent | absolute_price
  const [value, setValue] = useState(200);
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);

  const toggle = (slug) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setSelected(next);
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one service"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pricing-rules/apply-weekend", {
        service_slugs: Array.from(selected),
        mode,
        value: Number(value),
        replace_existing: replace,
      });
      onApplied(`Applied weekend rule to ${data.count} service(s)`);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#131A2B] border border-brand-orange/40" onClick={(e) => e.stopPropagation()} data-testid="bulk-weekend-modal">
        <div className="p-5 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Bulk Weekend Surcharge</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">Apply a weekend rule in one click</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              data-testid="bulk-weekend-mode"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm">
              <option value="surcharge_amount">Add flat ₹ to base (base + value)</option>
              <option value="surcharge_percent">Add % to base (base × (1 + value/100))</option>
              <option value="absolute_price">Set absolute weekend ₹ (overrides base)</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              Value {mode === "surcharge_percent" ? "(%)" : "(₹)"}
            </label>
            <input type="number" min="0" value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="bulk-weekend-value"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Apply to services</div>
            <div className="border border-white/12 max-h-48 overflow-y-auto">
              {services.map((s) => (
                <label key={s.slug} className="flex items-center gap-2 px-3 py-2 border-b border-white/6 last:border-0 cursor-pointer hover:bg-white/5">
                  <input type="checkbox" checked={selected.has(s.slug)} onChange={() => toggle(s.slug)}
                    data-testid={`bulk-weekend-svc-${s.slug}`} />
                  <span className="text-sm">{s.name}</span>
                  <span className="ml-auto text-[10px] font-mono text-white/40">base ₹{Math.round(s.effective_hourly_rate || s.unit_price || 0)}/hr</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
              data-testid="bulk-weekend-replace" />
            Overwrite existing weekend rules
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
            <button type="button" onClick={submit} disabled={busy}
              data-testid="bulk-weekend-apply"
              className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50">
              {busy ? "Applying…" : `Apply to ${selected.size} service(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
